import {clamp} from '@benchling/js-utils';
import assert from 'assert';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isRangeOverlap} from 'molbio/SeqUtil';
import {TranscriptionIndicesQuery} from 'molbio/transcriptions/__generated/TranscriptionIndicesQuery';

type RNASequenceGraphQL = ExtractByTypename<
  TranscriptionIndicesQuery['sequence'],
  'SequenceNode'
>['transcriptionMatches'][number]['rnaSequence'];

export interface SliceBoundedTranscription {
  clampedStart: number;
  clampedEnd: number;
  feature: TranscriptionMetadata;
}

export interface TranscriptionMetadata {
  isReverseComplement: boolean;
  start: number;
  end: number;
  rnaSequence: RNASequenceGraphQL;
}

export type TranscriptionIndices = Array<TranscriptionMetadata>;

/**
 * Return absolute coordinates of transcriptions bounded by slice start and stop indices
 */
export function sliceBoundedTranscriptions(
  sliceStart: number,
  sliceEnd: number,
  // If true, return reverse transcriptions. If false, return forward.
  isReverseComplement: boolean,
  transcriptions: Array<TranscriptionMetadata>
): Array<SliceBoundedTranscription> {
  if (Number.isNaN(sliceStart) || Number.isNaN(sliceEnd)) {
    // Return empty list if sliceStart or sliceEnd is NaN
    return [];
  }
  assert(
    sliceStart <= sliceEnd,
    `expected sliceStart ${sliceStart} <= sliceEnd ${sliceEnd} when parsing transcriptions`
  );
  const transcriptionsOnSlice = transcriptions.filter(
    (transcription) =>
      isRangeOverlap(transcription.start, transcription.end, sliceStart, sliceEnd) &&
      transcription.isReverseComplement === isReverseComplement
  );
  const sliceBoundedTranscriptions = transcriptionsOnSlice.map((transcription) => {
    const transcriptionStart = transcription.start;
    const transcriptionEnd = transcription.end;
    // Cross origin transcriptions should have an 'inverse clamp'. When Start or End are outside the given
    // slice, they are set to the opposite side of that window
    const invertedClampStart =
      transcriptionStart >= sliceStart && transcriptionStart < sliceEnd ? transcriptionStart : sliceStart;
    const invertedClampEnd =
      transcriptionEnd >= sliceStart && transcriptionEnd < sliceEnd ? transcriptionEnd : sliceEnd;
    return {
      clampedStart: clamp(sliceStart, sliceEnd, invertedClampStart),
      clampedEnd: clamp(sliceStart, sliceEnd, invertedClampEnd),
      feature: transcription,
    };
  });
  return sliceBoundedTranscriptions;
}
